import type { BootstrapConfig } from '../api/types';
import type { BuilderStep } from '../types/models';
import type { BuilderStore } from '../state/BuilderStore';
import { fetchModels, resolveImageUrl } from '../api/client';
import type { ModelSummary } from '../api/types';

/**
 * Step 1 – Collection: shows all models grouped by category.
 * Each category gets a heading followed by a grid of model cards.
 * Selecting a model advances the builder to the next step.
 */
export class StepCollection implements BuilderStep {
    readonly id = 'collection';
    readonly label = 'Model';

    private models: ModelSummary[] = [];
    private container: HTMLElement | null = null;

    constructor(
        private store: BuilderStore,
        public readonly config: BootstrapConfig,
        private navigateNext: (() => void) | null = null
    ) { }

    isEnabled(_config: BootstrapConfig): boolean {
        const s = _config.settings as Record<string, unknown>;
        return s.enableCollectionStep !== -1 && !s.directModelId;
    }

    async render(container: HTMLElement): Promise<void> {
        this.container = container;
        container.innerHTML = `
      <div class="vc-step vc-step-collection">
        <h2>Choose Your Model</h2>
        <div class="vc-collection-filters">
          <input type="text" id="vc-model-search" class="vc-input" placeholder="Search models..." />
        </div>
        <div id="vc-model-grid">
          <p class="vc-loading">Loading models...</p>
        </div>
      </div>
    `;

        try {
            this.models = await fetchModels();
            this.renderGroupedGrid(this.models);
            this.bindEvents();
        } catch (err) {
            const grid = container.querySelector('#vc-model-grid');
            if (grid) grid.innerHTML = `<p class="vc-error">Unable to load models. Please try again.</p>`;
        }
    }

    validate(): boolean {
        return this.store.getState().selectedModelId !== null;
    }

    onEnter(): void {
        // Clear all model-specific selections so options/colours from a
        // previous model don't bleed through when the user picks a new one.
        this.store.setState({
            selectedOptions: [],
            selectedColors: {},
            selectedEngineId: null,
        });
    }

    onLeave(): void { }

    destroy(): void {
        this.container = null;
        this.models = [];
    }

    // ── private ─────────────────────────────────────────────

    /** Group models by category and render each group with a heading + grid. */
    private renderGroupedGrid(models: ModelSummary[]): void {
        const wrapper = this.container?.querySelector('#vc-model-grid');
        if (!wrapper) return;

        if (models.length === 0) {
            wrapper.innerHTML = `<p class="vc-empty">No models found.</p>`;
            return;
        }

        // Build ordered map of category → models, preserving sort_order
        const groups = new Map<string, ModelSummary[]>();
        for (const m of models) {
            const cat = m.category || 'Other';
            if (!groups.has(cat)) groups.set(cat, []);
            groups.get(cat)!.push(m);
        }

        const selectedId = this.store.getState().selectedModelId;

        let html = '';
        for (const [category, catModels] of groups) {
            html += `<div class="vc-category-group">`;
            html += `<h3 class="vc-category-heading">${category}</h3>`;
            html += `<div class="vc-model-grid">`;
            html += catModels.map(m => `
                <div class="vc-model-card ${m.id === selectedId ? 'selected' : ''}" data-model-id="${m.id}">
                  <div class="vc-model-thumb">
                    <img src="${resolveImageUrl(m.thumbnail)}" alt="${m.model_name}" loading="lazy" />
                  </div>
                  <div class="vc-model-info">
                    <h3>${m.model_name}</h3>
                    <span class="vc-model-year">${m.model_year}</span>
                    ${m.length ? `<span class="vc-model-length">${m.length}'</span>` : ''}
                  </div>
                </div>
            `).join('');
            html += `</div></div>`;
        }

        wrapper.innerHTML = html;

        // Bind card clicks
        wrapper.querySelectorAll('.vc-model-card').forEach(card => {
            card.addEventListener('click', () => {
                const id = parseInt((card as HTMLElement).dataset.modelId ?? '0', 10);
                this.store.setState({ selectedModelId: id });
                if (this.navigateNext) {
                    this.navigateNext();
                } else {
                    // Fallback: just highlight if no navigation callback
                    wrapper.querySelectorAll('.vc-model-card').forEach(c => c.classList.remove('selected'));
                    card.classList.add('selected');
                }
            });
        });
    }

    private bindEvents(): void {
        const searchInput = this.container?.querySelector('#vc-model-search') as HTMLInputElement | null;

        searchInput?.addEventListener('input', () => {
            const search = (searchInput.value ?? '').toLowerCase();
            const filtered = this.models.filter(m =>
                !search || m.model_name.toLowerCase().includes(search) || (m.category ?? '').toLowerCase().includes(search)
            );
            this.renderGroupedGrid(filtered);
        });
    }
}
